import moment from 'moment'
import axiosInstance from './axiosConfig'
import { download, getExcelFileName } from '@app/utils/helpers'
import { createCancelToken } from './axiosUtils'

const SubmissionAPI = {
  getById: function ({ org_id, folder_id, submission_id }) {
    return axiosInstance.request({
      method: 'GET',
      url: `/submissions/${org_id}/${folder_id}/${submission_id}`,
    })
  },
  getAll: function ({ org_id, folder_id, isDelete, ...params }) {
    return axiosInstance.request({
      method: 'GET',
      url: `/folders/${org_id}/${folder_id}/submissions`,
      params: { ...params, delete: isDelete },
      cancelToken: cancelTokenObject[this.getAll.name].requestCancellation().token,
    })
  },
  update: function ({ org_id, folder_id, submission_id, ...data }) {
    return axiosInstance.request({
      method: 'PUT',
      url: `/submissions/${org_id}/${folder_id}/${submission_id}`,
      data: data,
    })
  },
  upload: function ({ org_id, folder_id, file, onUploadProgress }) {
    let formData = new FormData()
    formData.append('files', file)

    return axiosInstance.request({
      method: 'POST',
      url: `/folders/${org_id}/${folder_id}/submissions`,
      data: formData,
      onUploadProgress,
    })
  },
  uploadImages: function ({ org_id, folder_id, files, onUploadProgress }) {
    let formData = new FormData()
    files.forEach(f => {
      formData.append('files', f)
    })

    return axiosInstance.request({
      method: 'POST',
      url: `/folders/${org_id}/${folder_id}/images`,
      data: formData,
      onUploadProgress,
    })
  },
  delete: function ({ org_id, folder_id, submission_ids }) {
    return axiosInstance.request({
      method: 'DELETE',
      url: `/folders/${org_id}/${folder_id}/submissions`,
      data: { submission_ids },
    })
  },
  validate: function ({ org_id, folder_id, submission_id }) {
    return axiosInstance.request({
      method: 'PUT',
      url: `/submissions/${org_id}/${folder_id}/${submission_id}/validate`,
    })
  },
  verify: function ({ org_id, folder_id, submission_id }) {
    return axiosInstance.request({
      method: 'PUT',
      url: `/submissions/${org_id}/${folder_id}/${submission_id}/verify`,
    })
  },
  reject: function ({ org_id, folder_id, submission_id, reason }) {
    return axiosInstance.request({
      method: 'PUT',
      url: `/submissions/${org_id}/${folder_id}/${submission_id}/reject`,
      data: { note: reason },
    })
  },
  assign: function ({ org_id, folder_id, submission_id, user_id }) {
    return axiosInstance.request({
      method: 'PUT',
      url: `/submissions/${org_id}/${folder_id}/${submission_id}/assign`,
      data: { user_id },
    })
  },
  move: function ({ org_id, folder_id, submissions, folder }) {
    return axiosInstance.request({
      method: 'PUT',
      url: `/folders/${org_id}/${folder_id}/submissions/move`,
      data: { submissions, folder },
    })
  },
  getNumber: function ({ org_id, folder_id, ...params }) {
    return axiosInstance.request({
      method: 'GET',
      url: `/folders/${org_id}/${folder_id}/submissions-number-by-status`,
      params: params,
      cancelToken: cancelTokenObject[this.getNumber.name].requestCancellation().token,
    })
  },
  retryOCR: function ({ org_id, folder_id, submission_ids }) {
    return Promise.all(
      submission_ids.map(id => {
        axiosInstance.request({
          method: 'PUT',
          url: `/submissions/${org_id}/${folder_id}/${id}/ocr-retry`,
        })
      })
    )
  },
  getThumb: function ({ org_id, folder_id, submission_id }) {
    return axiosInstance
      .request({
        method: 'GET',
        url: `/submissions/${org_id}/${folder_id}/${submission_id}/image`,
        responseType: 'blob',
      })
      .then(res => {
        if (res.data?.type.indexOf('image') === -1) {
          return null
        }
        return { data: new Blob([res.data], { type: res.data?.type }) }
      })
  },
  validateMulti: function ({ org_id, folder_id, submission_ids }) {
    return axiosInstance.request({
      method: 'PUT',
      url: `/folders/${org_id}/${folder_id}/validate`,
      data: { submission_ids },
    })
  },
  verifyMulti: function ({ org_id, folder_id, submission_ids }) {
    return axiosInstance.request({
      method: 'PUT',
      url: `/folders/${org_id}/${folder_id}/verify`,
      data: { submission_ids },
    })
  },
  export: function ({ org_id, folder_id, folder_name, export_type = 'general', label_ids, submission_ids, ...params }) {
    let url = `/folders/${org_id}/${folder_id}/submissions/export-${export_type}`
    let data = {}
    if (label_ids) {
      data['label_ids'] = label_ids
    }
    if (submission_ids) {
      data['submission_ids'] = submission_ids
    }

    if (export_type === 'finance-report') {
      url = `/submissions/${org_id}/${folder_id}/${submission_ids[0]}/export-finance-report`
      data = { label_ids }
    }

    return axiosInstance
      .request({
        method: 'POST',
        url,
        data,
        params: params,
        responseType: 'blob',
      })
      .then(res => {
        const filename = getExcelFileName(folder_name)
        download(res.data, filename)
      })
  },
}

const cancelTokenObject = createCancelToken(SubmissionAPI)
export default SubmissionAPI
